import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MapPin, CalendarDays, Moon, Users, Download, Settings2, Bird, Mail, Phone,
  Globe, Globe2, Loader2, Upload, BedDouble, Compass, Ticket, Car, TrainFront, Plane, Sun, Sparkles,
  Lightbulb, Info, Utensils, AlertTriangle, FileText, CheckCircle2, Clock, ArrowRight,
  MessageCircle, ChevronDown, CreditCard, CalendarClock, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
// PayPal removido: los itinerarios de Viaja de Verdad no usan pago online.
// Chat, PWA y push eliminados: no aplican a los itinerarios de Viaja de Verdad.
import BookCallDialog from "@/pages/public/BookCallDialog";
import { fillPlaceholders, buildContext } from "@/lib/placeholders";

/* Silenciar errores cross-origin benignos del SDK de PayPal (overlay de desarrollo de CRA). */
if (typeof window !== "undefined" && !window.__etErrSuppressed) {
  window.__etErrSuppressed = true;
  const benign = (m, f) => (typeof m === "string" && m.includes("Script error")) || (typeof f === "string" && f.includes("paypal"));
  window.addEventListener("error", (e) => { if (benign(e.message, e.filename)) { e.stopImmediatePropagation(); e.preventDefault(); } }, true);
  window.addEventListener("unhandledrejection", (e) => { const r = String((e && e.reason) || ""); if (r.includes("paypal") || r.includes("Script error")) { e.stopImmediatePropagation(); e.preventDefault(); } }, true);
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
// Branding (Espíritu Travel · Viaja de Verdad) — resuelto a partir de `snap.brand`.
import { getBrand } from "@/lib/brand";

// Paleta editorial (referencia Fora): claro, cálido, tipografía serif + sans.
const C = { ink: "#1A1A1A", ink2: "#2C2C2C", sub: "#52525B", mute: "#A1A1AA", line: "#E4E4E7", bg: "#F9F8F6", surface: "#FFFFFF", navy: "#121B28", sand: "#F4EBD7", olive: "#8a9771" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const SANS = { fontFamily: "'Manrope', system-ui, sans-serif" };

const BOARD_LABELS = { sa: "Room only", bb: "Bed & breakfast", mp: "Half board", pc: "Full board" };
const TYPE_META = {
  alojamiento: { label: "Stay", Icon: BedDouble }, actividad: { label: "Experience", Icon: Compass },
  entradas: { label: "Tickets", Icon: Ticket }, transfer: { label: "Transfer", Icon: Car },
  rental_car: { label: "Self-Drive", Icon: Car },
  tren: { label: "Train", Icon: TrainFront }, vuelo: { label: "Flight", Icon: Plane }, free_day: { label: "Free day", Icon: Sun },
};
const NOTE_META = {
  suggestion: { label: "Suggestion", Icon: Lightbulb },
  city_info: { label: "Good to know", Icon: Info },
  restaurant: { label: "Where to eat", Icon: Utensils },
  driving: { label: "On the road", Icon: Car },
  tip: { label: "Travel tip", Icon: Sparkles },
  warning: { label: "Please note", Icon: AlertTriangle },
  info: { label: "Information", Icon: Info },
};
const eur = (n) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtDate = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d || ""; } };
const fmtShortDate = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d || ""; } };
const fmtWeekday = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" }); } catch { return ""; } };
const fmtLong = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" }); } catch { return d || ""; } };

const FontTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Manrope:wght@300;400;500;600;700&display=swap');
    .et-serif{font-family:'Cormorant Garamond',Georgia,serif;}
    .et-sans{font-family:'Manrope',system-ui,-apple-system,sans-serif;}
    .et-noscroll::-webkit-scrollbar{display:none}
    .et-noscroll{-ms-overflow-style:none;scrollbar-width:none}
    html{scroll-behavior:smooth}

    /* Estilos para el HTML embebido del prefacio (Traveler Info) — TD público. */
    .td-preface-content{color:#2C2C2C;font-family:'Manrope',system-ui,sans-serif;font-size:15px;line-height:1.65;}
    .td-preface-content h2{font-family:'Cormorant Garamond',Georgia,serif;font-size:1.75rem;font-weight:600;color:#121B28;margin:2rem 0 0.75rem;letter-spacing:-0.01em;line-height:1.15;}
    .td-preface-content h2:first-child{margin-top:0;}
    .td-preface-content h3{font-family:'Manrope',system-ui,sans-serif;font-size:1rem;font-weight:700;color:#1A1A1A;text-transform:uppercase;letter-spacing:0.06em;margin:1.5rem 0 0.5rem;}
    .td-preface-content p{margin:0.5rem 0;}
    .td-preface-content ul,.td-preface-content ol{margin:0.5rem 0 0.75rem 1.25rem;padding-left:1rem;}
    .td-preface-content ul{list-style:disc;} .td-preface-content ol{list-style:decimal;}
    .td-preface-content li{margin:0.25rem 0;}
    .td-preface-content strong{color:#121B28;font-weight:600;}
    .td-preface-content em{color:#52525B;}
    .td-preface-content a{color:#8a9771;text-decoration:underline;text-underline-offset:2px;font-weight:600;}
    .td-preface-content a:hover{color:#121B28;}
  `}</style>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const Overline = ({ children, className = "" }) => (
  <p className={`et-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-[#71717A] ${className}`}>{children}</p>
);

const ImageStrip = ({ images, alt, testid }) => {
  const imgs = (images || []).filter(Boolean).slice(0, 4);
  if (!imgs.length) return null;
  return (
    <div className={`grid gap-2 ${imgs.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={testid}>
      {imgs.map((u, i) => (
        <div key={i} className={`overflow-hidden rounded-sm ${imgs.length === 3 && i === 0 ? "col-span-2" : ""}`}>
          <img src={imgUrl(u)} alt={alt} loading="lazy" onError={(e) => { e.target.parentElement.style.display = "none"; }}
            className="h-full w-full object-cover transition-transform duration-[1200ms] hover:scale-105" style={{ aspectRatio: imgs.length === 1 ? "16/9" : "4/3" }} />
        </div>
      ))}
    </div>
  );
};

// Item del día: servicio (por tipo) o nota/sugerencia. Estilo editorial (columna etiqueta + contenido).
const OPTION_BADGE = {
  optional: { label: "Optional", cls: "border-[#8a9771] bg-[#8a9771]/10 text-[#5a6a4b]" },
  alternative: { label: "Alternative", cls: "border-[#A67B2D] bg-[#F4EBD7] text-[#8a5c1e]" },
};

const ContentItem = ({ Icon, label, title, html, text, images, testid, optionType }) => {
  const badge = optionType && OPTION_BADGE[optionType];
  return (
  <div className="flex flex-col gap-6 border-b border-[#E4E4E7] py-10 last:border-b-0 md:flex-row md:gap-12" data-testid={testid}>
    <div className="w-full flex-shrink-0 md:w-44">
      <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
    </div>
    <div className="min-w-0 flex-1">
      {badge && (
        <span className={`et-sans mb-3 inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${badge.cls}`} data-testid={`${testid}-optbadge`}>
          {badge.label}
        </span>
      )}
      {title ? <h3 className="et-serif text-2xl font-medium leading-snug text-[#1A1A1A] md:text-3xl">{title}</h3> : null}
      {html ? <div className="et-sans prose-sm mt-3 max-w-none text-base font-light leading-relaxed text-[#2C2C2C] [&_a]:underline [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_li]:pl-1 [&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:font-serif [&_h3]:text-lg [&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:text-base [&_h4]:font-semibold [&_strong]:font-semibold md:text-lg" style={{ color: C.ink2 }} dangerouslySetInnerHTML={{ __html: html }} /> : null}
      {text ? <p className="et-sans mt-3 whitespace-pre-line text-base font-light leading-relaxed text-[#2C2C2C] md:text-lg">{text}</p> : null}
      {images?.length ? <div className="mt-6"><ImageStrip images={images} alt={title || label} testid={`${testid}-img`} /></div> : null}
    </div>
  </div>
);
};

function PersonalizeDialog({ token, whitelabel, onSaved }) {
  const [open, setOpen] = useState(false);
  const [wl, setWl] = useState(whitelabel || {});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setWl(whitelabel || {}); }, [whitelabel]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await pub.post(`/trip/${token}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWl((w) => ({ ...w, logo_url: data.logo_url }));
      toast.success("Logo subido");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir el logo"); } finally { setUploading(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await pub.put(`/trip/${token}/whitelabel`, wl); onSaved(data.whitelabel); setOpen(false); toast.success("Branding updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setWl((w) => ({ ...w, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="et-sans gap-1.5 text-[#1A1A1A] hover:bg-black/5 print:hidden" data-testid="trip-personalize-btn">
          <Settings2 className="h-4 w-4" /> <span className="hidden sm:inline">Personalize</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" style={SANS}>
        <DialogHeader><DialogTitle style={SERIF} className="text-2xl">Make it yours</DialogTitle></DialogHeader>
        <p className="-mt-1 text-sm text-muted-foreground">Add your agency’s logo and contact details. Your travellers will see your brand on this itinerary.</p>
        <div className="space-y-3 py-1">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
              {wl.logo_url ? <img src={imgUrl(wl.logo_url)} alt="logo" className="h-full w-full object-contain" /> : <Bird className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="trip-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="trip-logo-upload">
                {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Upload logo
              </Button>
              <p className="mt-1 text-[11px] text-muted-foreground">PNG/JPG, max 5MB</p>
            </div>
          </div>
          <div><Label className="text-xs">Agency name</Label><Input value={wl.agency_name || ""} onChange={set("agency_name")} data-testid="trip-wl-agency" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Contact name</Label><Input value={wl.contact_name || ""} onChange={set("contact_name")} data-testid="trip-wl-contact" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={wl.phone || ""} onChange={set("phone")} data-testid="trip-wl-phone" /></div>
          </div>
          <div><Label className="text-xs">Email</Label><Input value={wl.email || ""} onChange={set("email")} data-testid="trip-wl-email" /></div>
          <div><Label className="text-xs">Website</Label><Input value={wl.website || ""} onChange={set("website")} placeholder="https://" data-testid="trip-wl-website" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-none bg-[#121B28] text-white hover:opacity-90" data-testid="trip-wl-save">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Save branding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Brandmark = ({ wl, enabled, brand }) => (
  enabled && wl?.logo_url ? (
    <img src={imgUrl(wl.logo_url)} alt={wl.agency_name || "logo"} className="h-6 w-auto max-w-[160px] object-contain sm:h-7" data-testid="trip-brand-logo" />
  ) : (
    <img src={brand.logo_navy} alt={brand.name} className="h-5 w-auto sm:h-6" data-testid="trip-brand-logo" />
  )
);

export default function PublicTripViewer() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [state, setState] = useState("loading");
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [payInfo, setPayInfo] = useState(null);
  const [ppClientId, setPpClientId] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const [paid, setPaid] = useState(null);
  const [active, setActive] = useState("");
  const sectionRefs = useRef({});

  const scrollTo = (id) => { const el = document.getElementById(id); if (el) window.scrollTo({ top: el.offsetTop - 76, behavior: "smooth" }); };
  const goToPay = () => scrollTo("reserve");

  useEffect(() => {
    let on = true;
    pub.get(`/trip/${token}`).then(({ data }) => { if (on) { setTrip(data); setApproved(!!data.approved); setState("ok"); } })
      .catch(() => { if (on) setState("error"); });
    pub.get(`/trip/${token}/payment`).then(({ data }) => { if (on) setPayInfo(data); }).catch(() => {});
    axios.get(`${BACKEND}/api/paypal/config`).then(({ data }) => { if (on) setPpClientId(data.client_id); }).catch(() => {});
    return () => { on = false; };
  }, [token]);

  useEffect(() => {
    if (trip?.title) document.title = `${trip.title} · Espíritu Travel`;
    return () => { document.title = "SOFI 2.0 — Plataforma de gestión"; };
  }, [trip?.title]);

  // Scrollspy para la navegación por días de la cabecera.
  useEffect(() => {
    if (state !== "ok") return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [state, trip]);

  const doApprove = async () => {
    setApproving(true);
    try {
      await pub.post(`/trip/${token}/approve`);
      setApproved(true); setApproveOpen(false);
      toast.success("Proposal approved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve the proposal"); }
    finally { setApproving(false); }
  };

  const wl = trip?.whitelabel || {};
  const wlEnabled = !!trip?.whitelabel_enabled;
  const hasWl = wlEnabled && wl && (wl.agency_name || wl.contact_name || wl.email || wl.phone || wl.website || wl.logo_url);
  // Marca aplicada al Trip Plan (elegida al publicar): "et" (default) o "vdv".
  const brand = getBrand(trip?.brand);
  // Nº correcto de días/noches a partir de las fechas del viaje (o duration_days / accommodations como fallback).
  const durationDays = useMemo(() => {
    if (trip?.start_date && trip?.end_date) {
      try {
        const d0 = new Date(`${String(trip.start_date).slice(0, 10)}T00:00:00`);
        const d1 = new Date(`${String(trip.end_date).slice(0, 10)}T00:00:00`);
        const diff = Math.round((d1 - d0) / 86400000) + 1;
        if (diff > 0) return diff;
      } catch { /* noop */ }
    }
    return trip?.duration_days || (trip?.days || []).length || 0;
  }, [trip]);
  const nights = useMemo(() => {
    // Preferir: (1) total_nights del snapshot (calculado por backend), (2) fechas del viaje,
    // (3) suma de noches de alojamientos (puede ser menor si no todas están cubiertas).
    if (typeof trip?.total_nights === "number" && trip.total_nights > 0) return trip.total_nights;
    if (durationDays > 0) return Math.max(0, durationDays - 1);
    const sumAcc = (trip?.accommodations || []).reduce((s, a) => s + (a.nights || 0), 0);
    return sumAcc;
  }, [trip, durationDays]);
  const showPrice = trip && trip.price_display !== "hidden" && (trip.price?.total || 0) > 0;
  const showPP = trip?.price_display === "total_pp";
  const isSold = !!(payInfo?.sold || paid);
  const canPay = !!payInfo?.payable && !isSold;
  const dueNow = payInfo?.amount ?? 0;
  const payFull = !!payInfo?.pay_in_full;
  const remaining = Math.max(0, (payInfo?.total ?? 0) - dueNow);
  const receiptUrl = paid?.receiptUrl || (payInfo?.receipt_url ? `${BACKEND}${payInfo.receipt_url}` : null);

  const navItems = useMemo(() => {
    if (!trip) return [];
    const items = (trip.days || []).map((d, i) => ({ id: `day-${i}`, label: d.date ? fmtDate(d.date) : (d.label || `Day ${d.day_number || i + 1}`) }));
    if ((trip.accommodations || []).length) items.push({ id: "stay", label: "Stay" });
    if ((trip.info_documents || []).length) items.push({ id: "info", label: "Info" });
    return items;
  }, [trip]);

  if (state === "loading") return (
    <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: C.bg }}>
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} />
    </div>
  );
  if (state === "error") return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center" style={{ backgroundColor: C.bg, color: C.ink }}>
      <FontTag />
      <Bird className="h-12 w-12" style={{ color: C.olive }} />
      <h1 className="et-serif text-3xl font-medium">Itinerary not available</h1>
      <p className="et-sans font-light opacity-70">This trip plan link is invalid or has been unpublished.</p>
    </div>
  );

  // ── MODO TRAVEL DOCUMENT ──────────────────────────────────────
  // Si el snapshot está marcado con mode="td", renderizamos el visor TD
  // reusando la paleta, tipografías y branding del cotizador.
  if (trip?.mode === "td") {
    return <TravelDocView trip={trip} token={token} wl={wl} wlEnabled={wlEnabled} brand={brand} />;
  }

  return (
    <div className="et-sans min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="public-trip-viewer">
      <FontTag />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#E4E4E7] bg-white/90 backdrop-blur-md print:static" data-testid="trip-header">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-3 sm:gap-3 sm:px-5 md:px-8 md:py-3.5">
          <div className="flex flex-shrink-0 items-center">
            <Brandmark wl={wl} enabled={wlEnabled} brand={brand} />
          </div>
          {navItems.length > 0 && (
            <nav className="et-noscroll mx-1 hidden flex-1 items-center gap-4 overflow-x-auto sm:flex md:gap-5 print:hidden" data-testid="trip-daynav">
              {navItems.map((n) => (
                <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`trip-nav-${n.id}`}
                  className={`et-sans whitespace-nowrap text-sm transition-colors ${active === n.id ? "font-semibold text-[#1A1A1A]" : "font-light text-[#A1A1AA] hover:text-[#52525B]"}`}>
                  {n.label}
                </button>
              ))}
            </nav>
          )}
          <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
            {canPay && (
              <button onClick={goToPay} data-testid="trip-header-book-btn"
                className="et-sans flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#121B28] px-3 py-1.5 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 sm:px-4 sm:py-2 sm:text-xs print:hidden">
                <CheckCircle2 className="h-3.5 w-3.5" /> <span className="hidden xs:inline">Reserve · </span>{eur(dueNow)}
              </button>
            )}
            {wlEnabled && <PersonalizeDialog token={token} whitelabel={wl} onSaved={(nw) => setTrip((t) => ({ ...t, whitelabel: nw }))} />}
            <a href={`${BACKEND}/api/public/trip/${token}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="trip-print-btn"
              className="et-sans flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#1A1A1A] hover:opacity-70 print:hidden">
              <Download className="h-4 w-4" /> <span className="hidden md:inline">Download PDF</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex h-[86vh] min-h-[520px] w-full items-end overflow-hidden print:h-auto print:min-h-0" style={{ backgroundColor: C.navy }} data-testid="trip-hero">
        {trip.hero_image && (
          <img src={imgUrl(trip.hero_image)} alt={trip.title} className="absolute inset-0 h-full w-full object-cover print:hidden" onError={(e) => { e.target.style.display = "none"; }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10 print:hidden" />
        {/* elemento decorativo vertical */}
        <div className="absolute left-6 top-1/2 hidden -translate-y-1/2 flex-col gap-2 md:flex print:hidden">
          {Array.from({ length: 14 }).map((_, i) => (<span key={i} className="block h-px bg-white/40" style={{ width: i % 4 === 0 ? 22 : 12 }} />))}
        </div>
        {/* specialist chip */}
        {trip.consultant?.name && (
          <div className="absolute right-5 top-5 flex items-center gap-3 rounded-full border border-white/20 bg-white/10 py-1.5 pl-1.5 pr-4 backdrop-blur-md md:right-8 md:top-8 print:hidden" data-testid="trip-consultant">
            {trip.consultant.photo && (
              <img src={imgUrl(trip.consultant.photo)} alt={trip.consultant.name} className="h-9 w-9 rounded-full object-cover ring-2 ring-white/50 sm:h-10 sm:w-10"
                onError={(e) => { e.target.style.display = "none"; }} data-testid="trip-consultant-photo" />
            )}
            <div className="leading-tight">
              <p className="et-sans text-[9px] font-semibold uppercase tracking-[0.18em] text-white/70">Your travel designer</p>
              <p className="et-sans text-xs font-semibold text-white" data-testid="trip-consultant-name">{trip.consultant.name}</p>
            </div>
          </div>
        )}
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-16 md:px-8 print:py-10">
          {trip.destination && (
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="et-sans mb-3 flex items-center gap-2 text-sm font-light text-white/90 print:text-[#1A1A1A]">
              <MapPin className="h-4 w-4" /> {trip.destination}
            </motion.p>
          )}
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="et-serif max-w-3xl text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl md:text-7xl print:text-[#1A1A1A]" data-testid="trip-title">{trip.title}</motion.h1>
          {trip.client_name && (
            <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.09 }}
              className="et-sans mt-3 text-sm font-light text-white/80 sm:text-base md:text-lg print:text-[#52525B]" data-testid="trip-client">
              <span className="et-sans mr-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60 md:text-[11px]">Prepared for</span>
              {trip.client_name}
            </motion.p>
          )}
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
            className="et-sans mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-light text-white/90 md:text-base print:text-[#52525B]">
            {trip.start_date && <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(trip.start_date)} – {fmtDate(trip.end_date)}</span>}
            <span className="flex items-center gap-2"><Moon className="h-4 w-4" />{durationDays} days{nights ? ` · ${nights} ${nights === 1 ? "night" : "nights"}` : ""}</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4" />{trip.num_travelers} travellers</span>
            {showPrice && <span className="flex items-center gap-2 font-medium text-white print:text-[#1A1A1A]">from {eur(showPP ? trip.price.per_person : trip.price.total)} {showPP ? "/ person" : "total"}</span>}
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70 print:hidden">
          <span className="et-sans text-[11px] uppercase tracking-[0.2em]">Scroll to view</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* Overview */}
      {trip.intro && (
        <section className="mx-auto max-w-3xl px-5 py-20 text-center md:px-8">
          <Reveal>
            <Overline>Your journey ahead</Overline>
            <p className="et-serif mt-4 text-2xl font-light leading-relaxed text-[#1A1A1A] md:text-3xl" data-testid="trip-intro">{trip.intro}</p>
          </Reveal>
        </section>
      )}

      {/* Trip at a glance — resumen día a día clickable */}
      {(trip.days || []).length > 0 && (
        <section className={`mx-auto max-w-3xl px-5 md:px-8 ${trip.intro ? "pb-24 md:pb-28" : "pt-24 pb-24 md:pt-28 md:pb-28"}`} data-testid="trip-glance">
          <Reveal>
            <Overline>Trip at a glance</Overline>
            <h2 className="et-serif mt-3 text-3xl font-medium tracking-tight text-[#1A1A1A] md:text-4xl">Your journey day by day</h2>
            <ol className="mt-10 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
              {(trip.days || []).map((d, i) => {
                // Título del día: user-title > label > city
                const dayHeadline = (d.title || d.label || d.city || "Free day").trim();
                // Highlights ordenados con icono por tipo (hotel primero, después activities/transfers)
                const rawHl = d.highlights || [];
                const uniqueHighlights = [];
                const seen = new Set();
                for (const h of rawHl) {
                  const nm = (h?.name || "").trim();
                  if (!nm) continue;
                  const key = `${h?.type || "act"}:${nm.toLowerCase()}`;
                  if (!seen.has(key)) { seen.add(key); uniqueHighlights.push({ type: h.type || "actividad", name: nm }); }
                }
                return (
                  <li key={i}>
                    <button onClick={() => scrollTo(`day-${i}`)} data-testid={`trip-glance-day-${i}`}
                      className="group grid w-full grid-cols-[auto_1fr] items-baseline gap-4 py-5 text-left transition-colors hover:bg-black/[.02] sm:grid-cols-[6rem_1fr] sm:gap-8 md:py-6">
                      <div className="self-start pt-1 sm:pt-2">
                        <span className="et-sans block text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8a9771] sm:text-[11px]">
                          Day {d.day_number || i + 1}
                        </span>
                        {d.date && <span className="et-sans mt-1 block text-[10px] font-light uppercase tracking-[0.15em] text-[#A1A1AA] sm:text-[11px]">{fmtShortDate(d.date)}</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="et-serif text-xl font-medium leading-snug text-[#1A1A1A] transition-colors group-hover:text-[#8a9771] md:text-2xl">
                          {dayHeadline}
                        </p>
                        {uniqueHighlights.length > 0 && (
                          <ul className="mt-2.5 space-y-1.5" data-testid={`trip-glance-highlights-${i}`}>
                            {uniqueHighlights.map((h, hi) => {
                              const Icon = h.type === "alojamiento"
                                ? BedDouble
                                : ((h.type === "transfer" || h.type === "rental_car") ? Car
                                   : (h.type === "vuelo" ? Plane
                                      : (h.type === "tren" ? TrainFront
                                         : (h.type === "entradas" ? Ticket : Compass))));
                              const isHotel = h.type === "alojamiento";
                              return (
                                <li key={hi} className="et-sans flex items-start gap-2 text-sm font-light text-[#52525B] md:text-[15px]">
                                  <Icon className={`mt-[3px] h-3.5 w-3.5 shrink-0 ${isHotel ? "text-[#8a9771]" : "text-[#A1A1AA]"}`} />
                                  <span className={isHotel ? "font-medium text-[#1A1A1A]" : ""}>{h.name}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </Reveal>
        </section>
      )}

      {/* Day by day */}
      <main className="mx-auto max-w-4xl px-5 md:px-12" data-testid="trip-itinerary">
        {(trip.days || []).map((d, i) => {
          const dayNum = d.day_number || i + 1;
          const dayTitle = d.title || d.label || "";
          return (
          <section key={i} id={`day-${i}`} ref={(el) => (sectionRefs.current[`day-${i}`] = el)}
            className="border-t border-[#E4E4E7] pb-4 pt-20 first:border-t-0" data-testid={`trip-day-${i}`}>
            <Reveal>
              <p className="et-sans flex flex-wrap items-baseline gap-x-3 gap-y-1 text-base font-bold uppercase tracking-[0.14em] text-[#1A1A1A] md:text-xl md:tracking-[0.16em]">
                <span className="text-[#8a9771]" data-testid={`trip-day-${i}-num`}>Day {dayNum}</span>
                {d.date && (<>
                  <span aria-hidden className="text-[#D4D4D8]">·</span>
                  <span className="font-medium text-[#71717A]">{fmtWeekday(d.date)} {fmtLong(d.date)}</span>
                </>)}
                {d.city && (<>
                  <span aria-hidden className="text-[#D4D4D8]">·</span>
                  <span>{d.city}</span>
                </>)}
              </p>
              {(() => {
                const t = (dayTitle || "").trim();
                const c = (d.city || "").trim();
                const showBigTitle = t && t.toLowerCase() !== c.toLowerCase();
                if (!showBigTitle) return null;
                return (
                  <h2 className="et-serif mt-3 text-3xl font-medium tracking-tight text-[#1A1A1A] md:text-4xl">
                    {t}
                  </h2>
                );
              })()}
            </Reveal>
            <div className="mt-6">
              {(d.services || []).length === 0 && (d.notes || []).length === 0 && (
                <p className="et-sans py-8 text-base font-light italic text-[#A1A1AA]">A free day to explore at your own pace.</p>
              )}
              {(d.services || []).map((s, si) => {
                const meta = TYPE_META[s.type] || TYPE_META.actividad;
                return <ContentItem key={`s${si}`} Icon={meta.Icon} label={meta.label} title={s.name} html={s.sales_description} images={s.images} optionType={s.option_type} testid={`trip-day-${i}-svc-${si}`} />;
              })}
              {(d.notes || []).map((n, ni) => {
                const meta = NOTE_META[n.icon] || NOTE_META.suggestion;
                return <ContentItem key={`n${ni}`} Icon={meta.Icon} label={meta.label} title={n.title} text={n.text} images={n.images} testid={`trip-day-${i}-note-${ni}`} />;
              })}
            </div>
          </section>
          );
        })}

        {/* Where you stay */}
        {(trip.accommodations || []).length > 0 && (
          <section id="stay" ref={(el) => (sectionRefs.current["stay"] = el)} className="border-t border-[#E4E4E7] pb-6 pt-20" data-testid="trip-stays">
            <Reveal>
              <Overline>Where you’ll stay</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Your accommodations</h2>
            </Reveal>
            <div className="mt-8 space-y-8">
              {trip.accommodations.map((a, i) => (
                <Reveal key={i} delay={0.03}>
                  <article className="border border-[#E4E4E7] bg-white p-6 md:p-10" data-testid={`trip-stay-${i}`}>
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                      <h3 className="et-serif text-3xl font-medium text-[#1A1A1A] md:text-4xl">{a.name}</h3>
                      <p className="et-sans flex items-center gap-3 text-sm font-light text-[#52525B]">
                        {a.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.city}</span>}
                        <span className="flex items-center gap-1"><Moon className="h-3.5 w-3.5" />{a.nights} {a.nights === 1 ? "night" : "nights"}</span>
                      </p>
                    </div>
                    <div className="mt-2"><span className="et-sans inline-block rounded-full border border-[#E4E4E7] px-3 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#52525B]">{BOARD_LABELS[a.board] || "Bed & breakfast"}</span></div>
                    {(a.images || []).length > 0 && <div className="mt-6"><ImageStrip images={a.images} alt={a.name} testid={`trip-stay-${i}-img`} /></div>}
                    {a.sales_description && <div className="et-sans prose-sm mt-6 max-w-none text-base font-light leading-relaxed text-[#2C2C2C] [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_strong]:font-semibold md:text-lg" dangerouslySetInnerHTML={{ __html: a.sales_description }} />}
                  </article>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Information & Documents */}
        {(trip.info_documents || []).length > 0 && (
          <section id="info" ref={(el) => (sectionRefs.current["info"] = el)} className="border-t border-[#E4E4E7] pb-16 pt-20" data-testid="trip-info-documents">
            <Reveal>
              <Overline>Good to know</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Information &amp; Documents</h2>
            </Reveal>
            <div className="mt-8 space-y-4">
              {trip.info_documents.map((s, i) => (
                <Reveal key={i} delay={0.02}>
                  <div className="border border-[#E4E4E7] bg-white p-6 md:p-8" data-testid={`trip-info-${i}`}>
                    <h3 className="et-serif text-2xl font-medium text-[#1A1A1A]">{s.title}</h3>
                    <div className="et-sans mt-2 whitespace-pre-line text-base font-light leading-relaxed text-[#2C2C2C]">{s.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Travel specialist */}
      {trip.consultant?.name && (
        <section className="mx-auto max-w-4xl px-5 pb-8 md:px-12" data-testid="trip-specialist">
          <div className="flex flex-col items-center gap-6 border border-[#E4E4E7] bg-white p-8 text-center md:flex-row md:gap-10 md:p-10 md:text-left">
            {trip.consultant.photo && <img src={imgUrl(trip.consultant.photo)} alt={trip.consultant.name} className="h-24 w-24 rounded-full object-cover ring-1 ring-[#E4E4E7]" onError={(e) => { e.target.style.display = "none"; }} />}
            <div className="flex-1">
              <Overline>Designed for you by</Overline>
              <h3 className="et-serif mt-1 text-3xl font-medium text-[#1A1A1A]">{trip.consultant.name}</h3>
              {trip.consultant.role && <p className="et-sans text-sm font-light text-[#52525B]">{trip.consultant.role}</p>}
              <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row md:justify-start">
                {trip.consultant.email && <a href={`mailto:${trip.consultant.email}`} className="et-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Mail className="h-4 w-4 text-[#A1A1AA]" />{trip.consultant.email}</a>}
                {trip.consultant.phone && <a href={`tel:${trip.consultant.phone}`} className="et-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Phone className="h-4 w-4 text-[#A1A1AA]" />{trip.consultant.phone}</a>}
              </div>
              <div className="mt-5">
                <BookCallDialog
                  token={token}
                  consultantName={trip.consultant.name}
                  trigger={
                    <button data-testid="trip-bookcall-btn"
                      className="et-sans inline-flex items-center gap-2 rounded-full border border-[#121B28] bg-transparent px-5 py-2.5 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#121B28] hover:text-white">
                      <CalendarClock className="h-4 w-4" /> Book a call
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reserve & pay (o aprobar, si el precio está oculto) */}
      <section id="reserve" className="mt-8 px-5 py-20 text-center text-white" style={{ backgroundColor: C.navy }} data-testid="trip-approve">
        <Reveal className="mx-auto max-w-xl">
          {isSold ? (
            <div data-testid="trip-paid-msg">
              <CheckCircle2 className="mx-auto mb-4 h-14 w-14" style={{ color: C.olive }} />
              <h2 className="et-serif text-4xl font-medium md:text-5xl">Booked — you're going!</h2>
              <p className="et-sans mx-auto mt-4 max-w-xl font-light leading-relaxed text-white/80">
                Thank you! We've received your payment{paid ? ` of ${eur(paid.amount)}` : (payInfo?.deposit_amount ? ` of ${eur(payInfo.deposit_amount)}` : "")}
                {(paid?.pct === 100 || payInfo?.deposit_pct === 100 || payInfo?.paid_in_full)
                  ? " — your trip is fully paid and confirmed. We can't wait to welcome you."
                  : ". Your deposit confirms your trip; the remaining balance is automatically scheduled before departure."}
              </p>
              {paid?.emailed && (
                <p className="et-sans mx-auto mt-3 max-w-xl text-sm font-light text-white/70" data-testid="trip-receipt-emailed">
                  <Mail className="mr-1.5 inline h-4 w-4" style={{ color: C.olive }} />
                  We've emailed your receipt{paid?.recipient ? ` to ${paid.recipient}` : ""}.
                </p>
              )}
              {receiptUrl && (
                <a href={receiptUrl} target="_blank" rel="noreferrer" data-testid="trip-receipt-btn"
                  className="et-sans mx-auto mt-6 inline-flex items-center justify-center gap-2 bg-white px-7 py-3.5 text-base font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                  <Download className="h-5 w-5" /> Download receipt
                </a>
              )}
              {!(paid?.pct === 100 || payInfo?.deposit_pct === 100 || payInfo?.paid_in_full) && remaining > 0 && (
                <div className="mx-auto mt-8 max-w-md" data-testid="trip-balance-bubble">
                  <div className="border border-white/15 bg-white/5 px-5 py-4 text-left">
                    <div className="et-sans flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: C.olive }}>
                      <MessageCircle className="h-4 w-4" /> Remaining balance
                    </div>
                    <p className="et-sans mt-2 text-sm font-light leading-relaxed text-white/85">
                      Your remaining balance of <strong className="font-semibold">{eur(remaining)}</strong> will be requested about <strong className="font-semibold">45 days before departure</strong>. We'll email you the details closer to the date — there's nothing you need to do right now.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : canPay ? (
            <>
              <Overline className="!text-white/60">Total Price</Overline>
              <div className="et-serif mt-3 text-6xl font-medium md:text-7xl" data-testid="trip-price">{eur(payInfo.total)}</div>
              <p className="et-sans mt-2 text-sm font-light text-white/70">total for your group · {trip.num_travelers} {trip.num_travelers === 1 ? "traveller" : "travellers"}</p>
              <div className="mx-auto mt-8 max-w-sm border border-white/15 p-6 text-left" data-testid="trip-duenow">
                <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">{payFull ? "Due now · full payment" : "Due now · 30% deposit"}</p>
                <p className="et-serif mt-1 text-4xl font-medium">{eur(dueNow)}</p>
                <p className="et-sans mt-2 text-xs font-light leading-relaxed text-white/70">
                  {payFull ? "Your trip starts within 60 days, so the full amount is due to confirm your booking." : `Reserve your dates today; the remaining ${eur(remaining)} is due 45 days before departure.`}
                </p>
                <div className="mt-5">
                  {!showPay ? (
                    <button onClick={() => setShowPay(true)} data-testid="trip-book-btn"
                      className="et-sans flex w-full items-center justify-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                      Book this trip · pay {eur(dueNow)} <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : ppClientId ? (
                    <a href={`mailto:${brand.email}`} className="et-sans flex w-full items-center justify-center gap-2 bg-white px-6 py-3.5 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                      Contact us to book
                    </a>
                  ) : (
                    <p className="et-sans text-xs font-light text-white/70">Loading secure payment…</p>
                  )}
                  {paying && (
                    <p className="et-sans mt-3 flex items-center justify-center gap-2 text-sm font-light text-white/85" data-testid="trip-paying">
                      <Clock className="h-4 w-4 animate-spin" style={{ color: C.olive }} /> Processing your payment…
                    </p>
                  )}
                  {payError && <p className="et-sans mt-3 text-sm" style={{ color: "#fecaca" }} data-testid="trip-pay-error">{payError}</p>}
                </div>
              </div>
              <p className="et-sans mt-4 text-[11px] font-light text-white/40">Secure payment · powered by PayPal</p>
              <a href={`/pay/${token}`} data-testid="trip-paylink" className="et-sans mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/70 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white">
                Prefer a deposit or a custom amount? Choose here <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </>
          ) : approved ? (
            <div data-testid="trip-approved-msg">
              <CheckCircle2 className="mx-auto mb-4 h-14 w-14" style={{ color: C.olive }} />
              <h2 className="et-serif text-4xl font-medium md:text-5xl">Itinerary accepted</h2>
              <p className="et-sans mx-auto mt-4 max-w-xl font-light leading-relaxed text-white/85">
                Thank you! You've accepted this itinerary. We'll email you an invoice to pay the deposit — your trip is only confirmed once the deposit has been received.
              </p>
            </div>
          ) : trip?.price_display === "hidden" ? null : (
            <>
              <Overline className="!text-white/60">Ready when you are</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium md:text-5xl">Happy with your journey?</h2>
              <p className="et-sans mx-auto mt-4 max-w-xl font-light leading-relaxed text-white/85">
                Approve this proposal and we'll send you the deposit invoice to secure your dates. Your trip is reserved once the deposit is paid.
              </p>
              <button onClick={() => setApproveOpen(true)} data-testid="trip-approve-btn"
                className="et-sans mt-7 inline-flex items-center gap-2 bg-white px-8 py-3.5 text-sm font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
                <CheckCircle2 className="h-5 w-5" /> Approve proposal
              </button>
            </>
          )}
        </Reveal>
      </section>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md" style={SANS}>
          <DialogHeader><DialogTitle style={SERIF} className="text-2xl">Approve this proposal?</DialogTitle></DialogHeader>
          <p className="-mt-1 text-sm text-muted-foreground">
            By approving, you confirm you're happy with this itinerary. We'll then email you an invoice to pay the deposit. Your trip is only reserved once the deposit is received.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} data-testid="trip-approve-cancel">Not yet</Button>
            <Button onClick={doApprove} disabled={approving} className="rounded-none bg-[#121B28] text-white hover:opacity-90" data-testid="trip-approve-confirm">
              {approving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Yes, approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="px-5 py-16 text-white/80" style={{ backgroundColor: C.navy }} data-testid="trip-footer">
        <div className="mx-auto max-w-3xl border-t border-white/10 pt-12 text-center">
          <p className="et-serif text-3xl font-light italic text-white">{brand.tagline}</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
            {(wl.email || brand.email) && <a href={`mailto:${wl.email || brand.email}`} className="et-sans flex items-center gap-2 font-light hover:opacity-80" data-testid="trip-footer-email"><Mail className="h-4 w-4" style={{ color: C.olive }} />{wl.email || brand.email}</a>}
            {(wl.phone || brand.phone) && <span className="et-sans flex items-center gap-2 font-light"><Phone className="h-4 w-4" style={{ color: C.olive }} />{wl.phone || brand.phone}</span>}
            {(wl.website || brand.website_url) && <a href={wl.website || brand.website_url} target="_blank" rel="noreferrer" className="et-sans flex items-center gap-2 font-light hover:opacity-80" data-testid="trip-footer-web"><Globe className="h-4 w-4" style={{ color: C.olive }} />{String(wl.website || brand.website).replace(/^https?:\/\//, "")}</a>}
          </div>
          {hasWl && (wl.agency_name || wl.contact_name) && (
            <p className="et-serif mt-6 text-2xl font-medium text-white">{wl.agency_name || wl.contact_name}</p>
          )}
          <div className="mx-auto mt-10 flex w-fit flex-col items-center gap-4">
            <span className="et-sans text-[11px] uppercase tracking-[0.3em] text-white/50">Crafted by</span>
            <img src={brand.logo_sand} alt={brand.name} className="h-12 w-auto sm:h-14 print:hidden" data-testid="trip-footer-logo" />
            <img src={brand.logo_navy} alt={brand.name} className="hidden h-12 w-auto print:block" data-testid="trip-footer-logo-print" />
          </div>
        </div>
      </footer>

    </div>
  );
}



/* ══════════════════════════════════════════════════════════════════════
 *  TravelDocView · visor público en modo TD (Fase 4 · feb-2026)
 *  Replica 1:1 la arquitectura del TP: header sticky + hero full-bleed
 *  + "Trip at a glance" (resumen día-por-día clickable) + día-por-día
 *  detallado + guías intercaladas + vouchers.
 * ═══════════════════════════════════════════════════════════════════ */
function TravelDocView({ trip, token, wl, wlEnabled, brand }) {
  const {
    main_traveler, destination, start_date, end_date,
    cover_image, general_notes, days = [], vouchers = [],
    prologue = [],
  } = trip || {};

  const nights = start_date && end_date
    ? Math.max(0, Math.round((new Date(end_date) - new Date(start_date)) / 86400000))
    : 0;
  const durationDays = nights ? nights + 1 : days.length;
  const numTravelers = trip?.num_travelers || 0;

  const sectionRefs = useRef({});
  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Prefacio "Traveler Info" (mismo comportamiento que TravelDocViewer.jsx).
  const [prefaceHtml, setPrefaceHtml] = useState("");
  useEffect(() => {
    let on = true;
    axios.get(`${BACKEND}/api/public/td-preface`)
      .then((r) => { if (on) setPrefaceHtml(r.data?.html || ""); })
      .catch(() => {});
    return () => { on = false; };
  }, []);
  const [prefaceOpen, setPrefaceOpen] = useState(true);
  const [prefaceRead, setPrefaceRead] = useState(false);
  useEffect(() => {
    try {
      const read = localStorage.getItem(`td-preface-read-${token}`) === "1";
      setPrefaceRead(read);
      setPrefaceOpen(!read);
    } catch { /* localStorage no disponible → mantener defaults */ }
  }, [token]);
  const togglePreface = () => {
    if (prefaceOpen && !prefaceRead) {
      const ok = window.confirm(
        "It's important that you read this information before your trip.\n\n" +
        "Are you sure you want to collapse it? You can always re-open it above."
      );
      if (!ok) return;
      try { localStorage.setItem(`td-preface-read-${token}`, "1"); } catch { /* ignore */ }
      setPrefaceRead(true);
    }
    setPrefaceOpen((v) => !v);
  };

  return (
    <div className="et-sans min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="public-td-view">
      <FontTag />

      {/* Header sticky — mismo patrón del TP: brandmark + nav días + Download */}
      <header className="sticky top-0 z-40 border-b border-[#E4E4E7] bg-white/90 backdrop-blur-md print:static">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-5 py-3 md:px-8 md:py-3.5">
          <Brandmark wl={wl} enabled={wlEnabled} brand={brand} />
          <nav className="et-noscroll ml-4 hidden flex-1 items-center gap-4 overflow-x-auto md:flex">
            {prefaceHtml && (
              <button
                onClick={() => scrollTo("traveler-info")}
                data-testid="td-nav-traveler-info"
                className="et-sans whitespace-nowrap text-sm font-light text-[#A1A1AA] transition-colors hover:text-[#52525B]"
              >
                Info
              </button>
            )}
            {days.map((d) => (
              <button
                key={d.day_num}
                onClick={() => scrollTo(`td-day-${d.day_num}`)}
                data-testid={`td-nav-day-${d.day_num}`}
                className="et-sans whitespace-nowrap text-sm font-light text-[#A1A1AA] transition-colors hover:text-[#52525B]"
              >
                {d.date ? fmtShortDate(d.date) : `Day ${d.day_num}`}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <a
              href={`${BACKEND}/api/public/trip/${token}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="td-print-btn"
              className="et-sans hidden items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#1A1A1A] transition-opacity hover:opacity-70 sm:flex print:hidden"
            >
              <Download className="h-4 w-4" />
              <span className="hidden md:inline">Download PDF</span>
            </a>
            <span className="et-sans hidden text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A1A1AA] md:inline">
              Travel Document
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative flex h-[86vh] min-h-[520px] w-full items-end overflow-hidden print:h-auto print:min-h-0"
        style={{ backgroundColor: C.navy }}
        data-testid="td-hero"
      >
        {cover_image && (
          <img
            src={imgUrl(cover_image)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover print:hidden"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10 print:hidden" />
        {/* Elemento decorativo vertical (mismo del TP) */}
        <div className="absolute left-6 top-1/2 hidden -translate-y-1/2 flex-col gap-2 md:flex print:hidden">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="block h-px bg-white/40" style={{ width: i % 4 === 0 ? 22 : 12 }} />
          ))}
        </div>
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-16 md:px-8 print:py-10">
          {destination && (
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="et-sans mb-3 flex items-center gap-2 text-sm font-light text-white/90 print:text-[#1A1A1A]"
            >
              <MapPin className="h-4 w-4" /> {destination}
            </motion.p>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="et-serif max-w-3xl text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl md:text-7xl print:text-[#1A1A1A]"
          >
            {main_traveler || "Your Journey"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.09 }}
            className="et-sans mt-3 text-sm font-light text-white/80 sm:text-base md:text-lg print:text-[#52525B]"
          >
            <span className="et-sans mr-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/60 md:text-[11px]">
              Travel Document
            </span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
            className="et-sans mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-light text-white/90 md:text-base print:text-[#52525B]"
          >
            {start_date && (
              <span className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />{fmtDate(start_date)}{end_date ? ` – ${fmtDate(end_date)}` : ""}
              </span>
            )}
            {durationDays > 0 && (
              <span className="flex items-center gap-2">
                <Moon className="h-4 w-4" />{durationDays} days{nights ? ` · ${nights} ${nights === 1 ? "night" : "nights"}` : ""}
              </span>
            )}
            {numTravelers > 0 && (
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />{numTravelers} traveller{numTravelers === 1 ? "" : "s"}
              </span>
            )}
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70 print:hidden">
          <span className="et-sans text-[11px] uppercase tracking-[0.2em]">Scroll to view</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* Traveler Info (prefacio colapsable) — se muestra ANTES del itinerario */}
      {prefaceHtml && (
        <section
          id="traveler-info"
          ref={(el) => (sectionRefs.current["traveler-info"] = el)}
          className="mx-auto max-w-4xl px-5 pb-4 pt-16 md:px-12"
          data-testid="td-traveler-info"
        >
          <Reveal>
            <button
              type="button"
              onClick={togglePreface}
              aria-expanded={prefaceOpen}
              className="group flex w-full items-center justify-between gap-3 text-left transition"
              data-testid="td-traveler-info-toggle"
            >
              <div className="flex items-center gap-3">
                <LifeBuoy className="h-5 w-5" style={{ color: C.olive }} />
                <div>
                  <Overline>Read before you go</Overline>
                  <h2 className="et-serif mt-1 text-3xl font-medium tracking-tight text-[#1A1A1A] md:text-4xl">
                    Traveler Info
                  </h2>
                </div>
              </div>
              <ChevronDown
                className={`h-6 w-6 flex-shrink-0 text-[#52525B] transition-transform duration-300 ${prefaceOpen ? "rotate-180" : ""}`}
              />
            </button>

            {!prefaceOpen && (
              <div
                className="mt-4 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid="td-traveler-info-hint"
              >
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>It's important that you read this information before your trip.</strong>{" "}
                  Click above to re-open it whenever you need.
                </span>
              </div>
            )}

            {prefaceOpen && (
              <div
                className="td-preface-content mt-6 rounded-md border border-[#E4E4E7] bg-white px-5 py-6 md:px-8 md:py-8"
                data-testid="td-traveler-info-content"
                dangerouslySetInnerHTML={{ __html: prefaceHtml }}
              />
            )}
          </Reveal>
        </section>
      )}

      {/* Trip at a glance — resumen día-por-día clickable */}
      {days.length > 0 && (
        <section className="mx-auto max-w-3xl px-5 pb-24 pt-24 md:px-8 md:pb-28 md:pt-28" data-testid="td-glance">
          <Reveal>
            <Overline>Trip at a glance</Overline>
            <h2 className="et-serif mt-3 text-3xl font-medium tracking-tight md:text-4xl" style={{ color: C.ink }}>
              Your journey day by day
            </h2>
            <ol className="mt-10 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
              {days.map((d) => {
                const dayHeadline = (d.city || "Free day").trim();
                // Highlights: primeros 4 items del día
                const highlights = (d.items || []).slice(0, 4).map((it) => ({
                  type: it.type || "actividad",
                  name: it.service || "",
                })).filter((h) => h.name);
                return (
                  <li key={d.day_num}>
                    <button
                      onClick={() => scrollTo(`td-day-${d.day_num}`)}
                      data-testid={`td-glance-day-${d.day_num}`}
                      className="group grid w-full grid-cols-[auto_1fr] items-baseline gap-4 py-5 text-left transition-colors hover:bg-black/[.02] sm:grid-cols-[6rem_1fr] sm:gap-8 md:py-6"
                    >
                      <div className="self-start pt-1 sm:pt-2">
                        <span className="et-sans block text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8a9771] sm:text-[11px]">
                          Day {d.day_num}
                        </span>
                        {d.date && (
                          <span className="et-sans mt-1 block text-[10px] font-light uppercase tracking-[0.15em] text-[#A1A1AA] sm:text-[11px]">
                            {fmtShortDate(d.date)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="et-serif text-xl font-medium leading-snug transition-colors group-hover:text-[#8a9771] md:text-2xl" style={{ color: C.ink }}>
                          {dayHeadline}
                        </p>
                        {highlights.length > 0 && (
                          <ul className="mt-2.5 space-y-1.5">
                            {highlights.map((h, hi) => {
                              const meta = TYPE_META[h.type] || TYPE_META.actividad;
                              const Icon = meta.Icon;
                              return (
                                <li key={hi} className="et-sans flex items-start gap-2 text-sm font-light text-[#52525B] md:text-[15px]">
                                  <Icon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[#A1A1AA]" />
                                  <span>{h.name}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </Reveal>
        </section>
      )}

      {/* Contenido principal · guías + días detallados */}
      <main className="mx-auto max-w-4xl px-5 md:px-12" data-testid="td-main">
        {general_notes && (
          <TdBlock overline="Information & Contact" testid="td-section-notes">
            <div
              className="et-sans max-w-none text-base font-light leading-relaxed [&_a]:underline [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 md:text-lg"
              style={{ color: C.ink2 }}
              dangerouslySetInnerHTML={{ __html: general_notes }}
            />
          </TdBlock>
        )}

        {(prologue || []).length > 0 && (
          <TdBlock overline={`About ${destination || "your trip"}`} title={`Welcome to ${destination || "your journey"}`} testid="td-section-prologue">
            {prologue.map((g, i) => (
              <TdGuideItem key={`prol-${g.id}-${i}`} guide={g} testid={`td-guide-${g.id}`} last={i === prologue.length - 1} />
            ))}
          </TdBlock>
        )}

        {days.map((day) => (
          <TdDay key={`day-${day.day_num}`} day={day} trip={trip} sectionRefs={sectionRefs} />
        ))}

        {(vouchers || []).length > 0 && (
          <TdBlock overline="Good to have" title="Attached vouchers" testid="td-section-vouchers">
            <div className="pt-6 grid gap-2">
              {vouchers.map((v) => <TdVoucherRow key={v.voucher_id} v={v} token={token} />)}
            </div>
          </TdBlock>
        )}

        <div className="border-t border-[#E4E4E7] pb-24 pt-20 text-center">
          <Overline>Bon voyage</Overline>
          <p className="et-serif mt-4 text-3xl font-medium italic" style={{ color: C.ink }}>
            Have a wonderful trip.
          </p>
        </div>
      </main>

      <footer className="border-t border-[#E4E4E7]" style={{ backgroundColor: C.surface }}>
        <div className="mx-auto max-w-4xl px-5 py-8 md:px-12 text-center et-sans text-xs" style={{ color: C.mute }}>
          © Espíritu Travel · Curated with care.
        </div>
      </footer>
    </div>
  );
}

// Bloque genérico con el patrón exacto del TP
function TdBlock({ overline, title, testid, children }) {
  return (
    <section className="border-t border-[#E4E4E7] pb-4 pt-20 first:border-t-0" data-testid={testid}>
      <Reveal>
        {overline && <Overline>{overline}</Overline>}
        {title && (
          <h2 className="et-serif mt-3 text-3xl font-medium tracking-tight md:text-4xl" style={{ color: C.ink }}>
            {title}
          </h2>
        )}
      </Reveal>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function TdGuideItem({ guide, testid, last }) {
  const Icon = _CAT_ICON[guide.category] || Info;
  const label = _CAT_LABEL_EN[guide.category] || "Guide";
  const suggestions = guide.suggestions || [];
  return (
    <div className={`flex flex-col gap-6 py-10 md:flex-row md:gap-12 ${last ? "" : "border-b border-[#E4E4E7]"}`} data-testid={testid}>
      <div className="w-full flex-shrink-0 md:w-44">
        <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
          <Icon className="h-3.5 w-3.5" /> {label}
        </p>
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="et-serif text-2xl font-medium leading-snug md:text-3xl" style={{ color: C.ink }}>{guide.name}</h3>
        {guide.description && (
          <div
            className="et-sans mt-3 max-w-none text-base font-light leading-relaxed [&_a]:underline [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_li]:pl-1 [&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:font-serif [&_h3]:text-lg [&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:text-base [&_h4]:font-semibold [&_strong]:font-semibold md:text-lg"
            style={{ color: C.ink2 }}
            dangerouslySetInnerHTML={{ __html: guide.description }}
          />
        )}
        {(guide.sales_images || []).length > 0 && (
          <div className="mt-6">
            <ImageStrip images={guide.sales_images} alt={guide.name} testid={`${testid}-img`} />
          </div>
        )}
        {suggestions.length > 0 && (
          <div className="mt-8 space-y-4" data-testid={`${testid}-suggestions`}>
            {suggestions
              .filter((s) => s.description || s.image || s.website)
              .map((s, i) => (
              <div
                key={`${testid}-sug-${i}`}
                className="flex flex-col gap-4 border-t border-[#E4E4E7] pt-4 md:flex-row"
                data-testid={`${testid}-suggestion-${i}`}
              >
                {s.image && (
                  <div className="w-full flex-shrink-0 overflow-hidden rounded md:w-40">
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="h-24 w-full object-cover md:h-24"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="et-serif text-lg font-medium leading-tight" style={{ color: C.ink }}>{s.name}</p>
                  {s.address && (
                    <p className="et-sans mt-1 text-xs font-light uppercase tracking-wider" style={{ color: C.mute }}>{s.address}</p>
                  )}
                  {s.description && (
                    <div
                      className="et-sans mt-2 text-sm font-light leading-relaxed [&_a]:underline [&_p]:my-1"
                      style={{ color: C.ink2 }}
                      dangerouslySetInnerHTML={{ __html: s.description }}
                    />
                  )}
                  {s.website && (
                    <a
                      href={s.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="et-sans mt-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:underline"
                      style={{ color: C.olive }}
                    >
                      <Globe2 className="h-3 w-3" /> Website
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TdDay({ day, trip, sectionRefs }) {
  const items = day.items || [];
  return (
    <section
      className="border-t border-[#E4E4E7] pb-4 pt-20 first:border-t-0"
      data-testid={`td-day-${day.day_num}`}
      id={`td-day-${day.day_num}`}
      ref={(el) => { if (sectionRefs) sectionRefs.current[`td-day-${day.day_num}`] = el; }}
    >
      <Reveal>
        <p className="et-sans flex flex-wrap items-baseline gap-x-3 gap-y-1 text-base font-bold uppercase tracking-[0.14em] md:text-xl md:tracking-[0.16em]" style={{ color: C.ink }}>
          <span style={{ color: C.olive }}>Day {day.day_num}</span>
          {day.date && (
            <>
              <span aria-hidden style={{ color: "#D4D4D8" }}>·</span>
              <span className="font-medium" style={{ color: "#71717A" }}>{fmtWeekday(day.date)} {fmtLong(day.date)}</span>
            </>
          )}
          {day.city && (
            <>
              <span aria-hidden style={{ color: "#D4D4D8" }}>·</span>
              <span>{day.city}</span>
            </>
          )}
        </p>
      </Reveal>
      <div className="mt-6">
        {items.length === 0 && (
          <p className="et-sans py-8 text-base font-light italic" style={{ color: "#A1A1AA" }}>
            A free day to explore at your own pace.
          </p>
        )}
        {items.map((item, si) => (
          <TdActivity
            key={item.reserva_id}
            item={item}
            trip={trip}
            testid={`td-day-${day.day_num}-svc-${si}`}
            last={si === items.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

function TdActivity({ item, trip, testid, last }) {
  // Guía intercalada dentro de un día — 2026-C.9
  if (item.item_type === "guide") {
    return (
      <div className={`py-8 ${last ? "" : "border-b border-[#E4E4E7]"}`} data-testid={testid}>
        <TdGuideItem guide={item} testid={`${testid}-content`} last />
      </div>
    );
  }

  const ctx = buildContext({
    reserva: {
      hour_entry: item.hour_entry, date_entry: item.date_entry,
      num_ad: item.num_ad, num_ch: item.num_ch, city: item.city,
      country: item.country,
      service: item.service, provider_name: item.provider_name, duration: item.duration,
      start_location: item.start_location, end_location: item.end_location,
    },
    trip,
  });
  const html = fillPlaceholders(item.sales_description || "", ctx);
  const hasContent = !!(item.sales_description || "").trim();
  const meta = TYPE_META[item.type] || TYPE_META.actividad;
  const Icon = meta.Icon;
  const isLodging = item.type === "alojamiento";
  const hc = item.hotel_card;
  const isCheckout = !!item.is_checkout;

  // Alojamiento · ficha estructurada (2026-C.8). Ignoramos plantilla libre.
  if (isLodging && hc) {
    if (isCheckout) {
      // Item minimal para el día de check-out: solo título.
      return (
        <div className={`flex flex-col gap-6 py-10 md:flex-row md:gap-12 ${last ? "" : "border-b border-[#E4E4E7]"}`} data-testid={testid}>
          <div className="w-full flex-shrink-0 md:w-44">
            <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
              <Icon className="h-3.5 w-3.5" /> {meta.label}
            </p>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="et-serif text-2xl font-medium leading-snug md:text-3xl" style={{ color: C.ink }}>Check out from {hc.hotel_name}</h3>
          </div>
        </div>
      );
    }
    return (
      <div className={`flex flex-col gap-6 py-10 md:flex-row md:gap-12 ${last ? "" : "border-b border-[#E4E4E7]"}`} data-testid={testid}>
        <div className="w-full flex-shrink-0 md:w-44">
          <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
            <Icon className="h-3.5 w-3.5" /> {meta.label}
          </p>
          {item.hour_entry && (
            <p className="et-sans mt-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.olive }}>
              {item.hour_entry}
            </p>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="et-serif text-2xl font-medium leading-snug md:text-3xl" style={{ color: C.ink }}>Check in at {hc.hotel_name}</h3>
          {hc.room && (
            <div className="mt-4 border-t border-[#E4E4E7] pt-4">
              <p className="et-sans text-xs uppercase tracking-wider text-[#71717A]">Room / Bed Type</p>
              <p className="et-sans mt-1 text-base font-medium" style={{ color: C.ink }}>{hc.room}</p>
            </div>
          )}
          <div className="mt-4 space-y-2 border-t border-[#E4E4E7] pt-4">
            {hc.holder_name && (
              <p className="et-sans text-base font-light" style={{ color: C.ink2 }}>The reservation is under the name: {hc.holder_name}</p>
            )}
            {hc.board_label && (
              <p className="et-sans text-base font-light" style={{ color: C.ink2 }}>{hc.board_label}</p>
            )}
          </div>
          {(hc.address || hc.website) && (
            <div className="mt-6 flex flex-col items-center">
              <p className="et-sans text-base font-semibold" style={{ color: C.ink }}>
                {hc.website ? (
                  <a href={hc.website} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-4">
                    {hc.hotel_name}
                  </a>
                ) : hc.hotel_name}
              </p>
              {hc.address && (
                <p className="et-sans mt-1 text-sm font-light underline decoration-dotted underline-offset-4" style={{ color: C.mute }}>{hc.address}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-6 py-10 md:flex-row md:gap-12 ${last ? "" : "border-b border-[#E4E4E7]"}`} data-testid={testid}>
      <div className="w-full flex-shrink-0 md:w-44">
        <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </p>
        {item.hour_entry && (
          <p className="et-sans mt-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.olive }}>
            {item.hour_entry}{item.duration ? ` · ${item.duration}` : ""}
          </p>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="et-serif text-2xl font-medium leading-snug md:text-3xl" style={{ color: C.ink }}>{item.service || "Activity"}</h3>
        {hasContent ? (
          <div
            className="et-sans mt-3 max-w-none text-base font-light leading-relaxed [&_a]:underline [&_p]:mb-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_li]:pl-1 [&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:font-serif [&_h3]:text-lg [&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:text-base [&_h4]:font-semibold [&_strong]:font-semibold md:text-lg"
            style={{ color: C.ink2 }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <p className="et-sans mt-3 text-base font-light italic" style={{ color: C.mute }}>
            Details coming soon.
          </p>
        )}
        {(item.sales_images || []).length > 0 && (
          <div className="mt-6">
            <ImageStrip images={item.sales_images} alt={item.service} testid={`${testid}-img`} />
          </div>
        )}
      </div>
    </div>
  );
}

function TdVoucherRow({ v, token }) {
  const href = v.voucher_id ? `${BACKEND}/api/public/trip/${token}/voucher/${v.voucher_id}` : "#";
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-sm border border-[#E4E4E7] bg-white px-4 py-3 transition-colors hover:bg-[#F9F8F6]"
      data-testid={`td-voucher-${v.voucher_id}`}
    >
      <Download className="h-4 w-4" style={{ color: C.olive }} />
      <div className="min-w-0 flex-1">
        <div className="et-sans text-sm font-medium truncate" style={{ color: C.ink }}>{v.filename}</div>
        <div className="et-sans text-xs font-light" style={{ color: C.sub }}>
          {v.reserva_service}{v.reserva_date ? ` · ${v.reserva_date}` : ""}
        </div>
      </div>
    </a>
  );
}

const _CAT_ICON = {
  city_guide: Compass, restaurants: Utensils, bars_nightlife: Sparkles,
  museums: Ticket, activities: Compass, transportation: Car,
  shopping: Sparkles, general_info: Info, other: Info,
};
const _CAT_LABEL_EN = {
  city_guide: "City guide", restaurants: "Where to eat",
  bars_nightlife: "Nightlife", museums: "Museums", activities: "Experience",
  transportation: "Getting around", shopping: "Shopping",
  general_info: "Good to know", other: "Notes",
};
